import './DetailsTable.scss';

import ChangeRecords from './ChangeRecord';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DetailsExportButton } from './DetailsExportButton';
import { DetailsSummarizeButton } from './DetailsSummarizeButton';
import { getTimelineDetailColumns } from './DetailsTableColumns';
import { isRowExpandable } from './@arch/lib';
import { SonarCard } from '@ui/general';
import { Space } from 'antd';
import { TimelineFiltersParams } from './filters/TimelineFilters';

import { getTimelineDetailsSelector, getTimelineSummarySelector } from './@arch/selectors';
import { KeyPaginationQueryParams, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { PaginatedTable, PaginatedTableQueryParams } from '@ui/tables';
import React, { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { TimelineDetailsTableColumnKey, TimelineTableEntry } from '@sonar-software/sonar-core';
import { UpdateQueryParams, usePeriscopeActions, usePrevious, useSonarSelector } from '@hooks';

const tableColumns = getTimelineDetailColumns();

export type TimelineDetailsQueryParams = PaginatedTableQueryParams<TimelineDetailsTableColumnKey> &
	TimelineFiltersParams &
	{ tzoffset: number };

export interface TimelineDetailsTableProps {
	onChange: UpdateQueryParams<KeyPaginationQueryParams>
	params: TimelineDetailsQueryParams;
}

const renderChangeRecords = (record: TimelineTableEntry) => <ChangeRecords record={ record } />;
const renderRowKey = (record: TimelineTableEntry) => record.change.id;

export const TimelineDetailsTable: React.FC<TimelineDetailsTableProps> = ({ params, onChange }) => {
	const summary = useSonarSelector(getTimelineSummarySelector.data);
	const disabled = !Object.keys(summary?.changeCounts ?? {}).length;
	const { openPeriscope } = usePeriscopeActions();
	const [ expandedRowKeys, setExpandedRowKeys ] = useState<Key[]>([]);
	const previousParams = usePrevious(params);

	const resource = useSonarSelector(getTimelineDetailsSelector.data);
	const areDetailsLoading = useSonarSelector(getTimelineDetailsSelector.isRequested);

	const onExpandedRowsChange = useCallback((keys: React.Key[]) => setExpandedRowKeys([ ...keys ]), []);

	const onRowClick = useCallback(
		(record: TimelineTableEntry) => {
			if (record.isVanished || record.sonarObjectType === SonarObjectType.SalesforceProfile) {
				return;
			}

			openPeriscope({
				id: record.sonarObjectId,
				name: record.displayName ?? '',
				openedFromIndex: -1,
				type: record.sonarObjectType
			});
		},
		[ openPeriscope ]
	);

	const columns = useMemo(() => tableColumns.map((column) => ({
		...column,
		sorter: !disabled
	})), [ disabled ]);

	useEffect(() => {
		if (params !== previousParams) {
			setExpandedRowKeys([]);
		}
	}, [ previousParams, params ]);

	return (
		<SonarCard
			extra={
				<Space>
					<DetailsExportButton
						disabled={ disabled }
						queryParams={ params } />
					<DetailsSummarizeButton
						disabled={ disabled }
						queryParams={ params } />
				</Space>
			}
			id='timeline-details'
			loading={ !resource }
			noSpacing
			title={ COPY_SPECIFIC.DETAILED_LIST_OF_CHANGES }
			variant='borderless'>
			<PaginatedTable
				columns={ columns }
				expandable={ {
					expandedRowKeys: expandedRowKeys,
					expandedRowRender: renderChangeRecords,
					onExpandedRowsChange: onExpandedRowsChange,
					rowExpandable: isRowExpandable
				} }
				loading={ areDetailsLoading }
				onChange={ onChange }
				onRow={ (record) => ({
					className:
						record.isVanished || record.sonarObjectType === SonarObjectType.SalesforceProfile ?
							'' :
							'clickable',
					onClick: () => onRowClick(record)
				}) }
				resource={ resource }
				rowKey={ renderRowKey } />
		</SonarCard>
	);
};
